import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquarePlus, Send, Star, Lightbulb, Bug, Heart, Sparkles } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const KINDS = [
  { key: 'idea', label: 'Idea',   Icon: Lightbulb, tone: 'text-[hsl(var(--primary))]' },
  { key: 'issue', label: 'Issue', Icon: Bug,       tone: 'text-[hsl(var(--danger))]' },
  { key: 'praise', label: 'Praise', Icon: Heart,   tone: 'text-[hsl(var(--success))]' },
  { key: 'other', label: 'Other', Icon: Sparkles,  tone: 'text-[hsl(var(--foreground-muted))]' },
];

/** Bottom-right floating "Feedback" pill. Hidden on auth/login screens.
 *  Opens a lightweight dialog with kind + rating + body. */
export function FeedbackButton() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Hide on unauthenticated pages entirely.
  const HIDDEN_PATHS = ['/login', '/register', '/pending', '/wrong-domain'];
  if (!user || HIDDEN_PATHS.some(p => location.pathname.startsWith(p))) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="feedback-fab"
        aria-label="Send feedback"
        className="fixed bottom-4 right-4 z-40 flex items-center gap-1.5 px-3.5 py-2 rounded-full
                   shadow-[0_10px_30px_-8px_hsl(var(--primary)/.55)]
                   bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]
                   text-[12.5px] font-semibold tracking-tight
                   hover:translate-y-[-1px] hover:shadow-[0_14px_36px_-8px_hsl(var(--primary)/.65)]
                   active:translate-y-0 transition-all duration-150"
      >
        <MessageSquarePlus size={14} strokeWidth={2.3} />
        Feedback
      </button>
      <FeedbackDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

function FeedbackDialog({ open, onOpenChange }) {
  const location = useLocation();
  const [body, setBody] = useState('');
  const [kind, setKind] = useState('idea');
  const [rating, setRating] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pageLabel, setPageLabel] = useState('');

  useEffect(() => {
    if (open) {
      setBody(''); setKind('idea'); setRating(null); setBusy(false);
      const doc = document?.title || '';
      setPageLabel(doc.replace(/32DECEMBER.*?[·|]\s*/i, '').trim() || location.pathname);
    }
  }, [open, location.pathname]);

  const submit = async () => {
    const trimmed = body.trim();
    if (trimmed.length < 3) {
      toast.error('Please write a couple of words first.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/feedback', {
        body: trimmed, kind, rating,
        page_url: location.pathname + location.search,
        page_label: pageLabel,
      });
      toast.success('Thanks — noted. This shapes V03.');
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not send. Try again in a bit.';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px]" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquarePlus size={16} className="text-[hsl(var(--primary))]" />
            Tell us anything
          </DialogTitle>
          <DialogDescription>
            What worked, what didn&rsquo;t, what would you love next? Every note helps us scope Version 03.
          </DialogDescription>
        </DialogHeader>

        {/* Kind chips */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="feedback-kind-row">
          {KINDS.map(k => {
            const active = kind === k.key;
            const Icon = k.Icon;
            return (
              <button
                key={k.key}
                type="button"
                onClick={() => setKind(k.key)}
                data-testid={`feedback-kind-${k.key}`}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[12px] transition-all
                            ${active
                              ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/.10)] text-[hsl(var(--foreground))]'
                              : 'border-[hsl(var(--border))] text-[hsl(var(--foreground-muted))] hover:bg-[hsl(var(--surface-2))]'}`}
              >
                <Icon size={12} className={active ? 'text-[hsl(var(--primary))]' : k.tone} />
                {k.label}
              </button>
            );
          })}
        </div>

        <Textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder="What&rsquo;s on your mind? (Please be specific — the more concrete, the better.)"
          rows={5}
          data-testid="feedback-body"
          className="text-[13.5px]"
        />

        {/* Rating stars */}
        <div className="flex items-center gap-2 text-[12px] text-[hsl(var(--foreground-muted))]">
          <span>How would you rate today?</span>
          <div className="flex items-center gap-0.5" data-testid="feedback-rating-row">
            {[1, 2, 3, 4, 5].map(n => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(rating === n ? null : n)}
                aria-label={`Rate ${n}`}
                data-testid={`feedback-star-${n}`}
                className="p-0.5 rounded hover:bg-[hsl(var(--surface-2))] transition-all"
              >
                <Star
                  size={16}
                  className={n <= (rating || 0)
                    ? 'fill-[hsl(var(--warning))] text-[hsl(var(--warning))]'
                    : 'text-[hsl(var(--foreground-subtle))]'}
                />
              </button>
            ))}
          </div>
        </div>

        <div className="text-[11px] text-[hsl(var(--foreground-subtle))]">
          Page: <span className="font-mono">{pageLabel}</span>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="feedback-cancel">
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={busy || body.trim().length < 3} data-testid="feedback-submit">
            <Send size={13} className="mr-1.5" />
            {busy ? 'Sending…' : 'Send feedback'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
